import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';

/**
 * Shared modal for creating or editing a Pulse client.
 * - onSaved(client) called after a successful POST/PUT with the server-returned row.
 * - If `client` is null/undefined, acts as a create form.
 */
export default function ClientFormDialog({ open, onClose, onSaved, client }) {
  const isEdit = !!client?.client_id;
  const [form, setForm] = useState(() => ({
    legal_name:           client?.legal_name || '',
    trading_name:         client?.trading_name || '',
    billing_contact_name: client?.billing_contact_name || '',
    billing_email:        client?.billing_email || '',
    phone:                client?.phone || '',
    address_line_1:       client?.address_line_1 || '',
    address_line_2:       client?.address_line_2 || '',
    city:                 client?.city || '',
    state_region:         client?.state_region || '',
    postal_code:          client?.postal_code || '',
    country:              client?.country || '',
    account_number:       client?.account_number || '',
    currency_code:        client?.currency_code || 'USD',
    payment_terms_days:   client?.payment_terms_days ?? 30,
    status:               client?.status || 'active',
    notes:                client?.notes || '',
  }));
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const setField = (k) => (e) => {
    const v = e.target.type === 'number' ? (e.target.value ? Number(e.target.value) : null) : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.legal_name?.trim()) {
      toast.error('Legal name is required');
      return;
    }
    setSaving(true);
    // Drop empty strings so we send nulls rather than overwriting with blanks
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined));
    try {
      const res = isEdit
        ? await api.put(`/api/clients/${client.client_id}`, payload)
        : await api.post('/api/clients', payload);
      toast.success(isEdit ? 'Client updated in Pulse' : 'Client created in Pulse');
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-start justify-center z-50 overflow-y-auto" data-testid="client-form-dialog" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-2xl my-8 rounded-lg border border-slate-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isEdit ? 'Edit Client' : 'New Client'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="client-form-close"><X size={16}/></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

          <SectionTitle>Identity</SectionTitle>
          <Row>
            <Field label="Legal Name *" htmlFor="legal_name" required>
              <input id="legal_name" name="legal_name" className={inputCls} value={form.legal_name} onChange={setField('legal_name')} data-testid="client-form-legal-name" />
            </Field>
            <Field label="Trading Name" htmlFor="trading_name">
              <input id="trading_name" name="trading_name" className={inputCls} value={form.trading_name} onChange={setField('trading_name')} data-testid="client-form-trading-name" />
            </Field>
          </Row>
          <Row>
            <Field label="Client / Account Number" htmlFor="account_number">
              <input id="account_number" name="account_number" className={inputCls} value={form.account_number} onChange={setField('account_number')} data-testid="client-form-account-number" />
            </Field>
            <Field label="Status" htmlFor="status">
              <select id="status" name="status" className={inputCls} value={form.status} onChange={setField('status')} data-testid="client-form-status">
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="pending">pending</option>
                <option value="on_hold">on_hold</option>
              </select>
            </Field>
          </Row>

          <SectionTitle>Billing</SectionTitle>
          <Row>
            <Field label="Billing Contact" htmlFor="billing_contact_name">
              <input id="billing_contact_name" name="billing_contact_name" className={inputCls} value={form.billing_contact_name} onChange={setField('billing_contact_name')} data-testid="client-form-billing-contact" />
            </Field>
            <Field label="Billing Email" htmlFor="billing_email">
              <input id="billing_email" name="billing_email" type="email" className={inputCls} value={form.billing_email} onChange={setField('billing_email')} data-testid="client-form-billing-email" />
            </Field>
          </Row>
          <Row>
            <Field label="Phone" htmlFor="phone">
              <input id="phone" name="phone" className={inputCls} value={form.phone} onChange={setField('phone')} data-testid="client-form-phone" />
            </Field>
            <Field label="Currency" htmlFor="currency_code">
              <select id="currency_code" name="currency_code" className={inputCls} value={form.currency_code} onChange={setField('currency_code')} data-testid="client-form-currency">
                {['USD','GBP','EUR','AUD','CAD','SGD','JPY','CHF','INR'].map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Payment Terms (days)" htmlFor="payment_terms_days">
              <input id="payment_terms_days" name="payment_terms_days" type="number" className={inputCls} value={form.payment_terms_days ?? ''} onChange={setField('payment_terms_days')} data-testid="client-form-payment-terms" />
            </Field>
            <Field label="&nbsp;" />
          </Row>

          <SectionTitle>Address</SectionTitle>
          <Field label="Address Line 1" htmlFor="address_line_1">
            <input id="address_line_1" name="address_line_1" className={inputCls} value={form.address_line_1} onChange={setField('address_line_1')} data-testid="client-form-address-1" />
          </Field>
          <Field label="Address Line 2" htmlFor="address_line_2">
            <input id="address_line_2" name="address_line_2" className={inputCls} value={form.address_line_2} onChange={setField('address_line_2')} data-testid="client-form-address-2" />
          </Field>
          <Row>
            <Field label="City" htmlFor="city">
              <input id="city" name="city" className={inputCls} value={form.city} onChange={setField('city')} data-testid="client-form-city" />
            </Field>
            <Field label="State / Region" htmlFor="state_region">
              <input id="state_region" name="state_region" className={inputCls} value={form.state_region} onChange={setField('state_region')} data-testid="client-form-state" />
            </Field>
          </Row>
          <Row>
            <Field label="Postal Code" htmlFor="postal_code">
              <input id="postal_code" name="postal_code" className={inputCls} value={form.postal_code} onChange={setField('postal_code')} data-testid="client-form-postal-code" />
            </Field>
            <Field label="Country" htmlFor="country">
              <input id="country" name="country" className={inputCls} value={form.country} onChange={setField('country')} data-testid="client-form-country" />
            </Field>
          </Row>

          <SectionTitle>Notes</SectionTitle>
          <Field label="Notes" htmlFor="notes">
            <textarea id="notes" name="notes" className={inputCls} rows={3} value={form.notes} onChange={setField('notes')} data-testid="client-form-notes" />
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs rounded-md hover:bg-slate-50"
              data-testid="client-form-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-1.5 bg-indigo-900 text-white text-xs font-semibold rounded-md hover:bg-indigo-800 disabled:opacity-50"
              data-testid="client-form-submit">
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-400";

function SectionTitle({ children }) {
  return <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider pt-2 border-t border-slate-100 first:border-0 first:pt-0">{children}</p>;
}

function Row({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children, required, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={`block text-xs mb-0.5 ${required ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>{label}</label>
      {children}
    </div>
  );
}
